import { Stack } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { isCurrentBeneficiaryEntityKind } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { PresentationDataTableSlide } from '@/modules/presentation/clientPresentation/components/PresentationDataTableSlide';
import { ClientPresentationDesignerV2_EntityFragment } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';
import { PresentationHoverRow } from '@/modules/presentation/components/PresentationHoverActions/PresentationHoverRow';
import { getBeneficiaryDesignationsFromEntity } from '@/modules/presentation/entities/components/BeneficiaryDesignationTable/getBeneficiaryDesignationsFromEntity';
import { GeneratedDispositionScenarioSlideProps } from '@/modules/presentation/entities/components/DispositionScenarioSlide/DispositionScenarioSlide.utils';
import { DispositionScenarioTable } from '@/modules/presentation/entities/components/DispositionScenarioSlide/DispositionScenarioTable';
import { RowData as DispositionRowData } from '@/modules/presentation/entities/components/DispositionScenarioSlide/DispositionScenarioTable.types';
import { PersonalAccountBeneficiariesV2 } from '@/modules/presentation/entities/components/summarySlides/PersonalAccount.BeneficiariesV2';
import { EntitySummaryGrantor } from '@/modules/presentation/entities/entityPresentations.types';
import { PRESENTATION_SPACING_UNITS } from '@/modules/presentation/presentation.constants';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { RowData as BeneficiaryRowData } from '@/modules/trusts/TrustBeneficiariesList/FlatBeneficiaryTable.types';
import { EntityKind } from '@/types/schema';

export interface EntityBeneficiariesAndDispositionsSlideProps {
  entity: ClientPresentationDesignerV2_EntityFragment;
  slideId: string;
  footer?: React.ReactNode;
  grantors: EntitySummaryGrantor[];

  beneficiariesRows: BeneficiaryRowData[] | null;
  dispositionRowsByDeath: [DispositionRowData[], DispositionRowData[]] | null;
  dispositionScenario?: GeneratedDispositionScenarioSlideProps;
  hideBeneficiaries?: boolean;

  /**
   * Edit-mode floating toolbars for the design surface (supplied by the
   * presentation-aware wrapper). Each card is wrapped in a hover region only
   * when its toolbar is present; the hover treatment is inert outside the
   * presentation hover provider, so the entity preview modal and print paths
   * (which also render this slide) are unaffected.
   */
  currentAccessToolbar?: ReactNode;
  dispositionsToolbar?: ReactNode;
}

export function EntityBeneficiariesAndDispositionsSlide({
  entity,
  slideId,
  footer,
  beneficiariesRows,
  dispositionRowsByDeath,
  dispositionScenario,
  grantors,
  hideBeneficiaries,
  currentAccessToolbar,
  dispositionsToolbar,
}: EntityBeneficiariesAndDispositionsSlideProps) {
  const groupHeader: string | undefined = useMemo(
    () =>
      isCurrentBeneficiaryEntityKind(entity.kind) ? 'Individual' : undefined,
    [entity.kind]
  );

  // Retirement / DH-insurance accounts render the BDG tier table.
  const useBeneficiaryDesignations = isBeneficiaryDesignationEntity(
    entity.kind
  );
  const beneficiaryDesignations = useMemo(
    () =>
      useBeneficiaryDesignations
        ? getBeneficiaryDesignationsFromEntity(entity)
        : null,
    [entity, useBeneficiaryDesignations]
  );

  // Life insurance entities have no dedicated "current beneficiaries" section,
  // so the table is always empty for them — omit it entirely.
  const hideCurrentBeneficiaries =
    hideBeneficiaries || entity.kind === EntityKind.InsurancePersonalAccount;

  // Mirror each table's own render-or-null logic so we only mount a hover
  // region around a card that actually renders content (an empty region would
  // be a hoverable blank box).
  const showCurrentAccess =
    !hideCurrentBeneficiaries && beneficiariesRows !== null;
  const showDispositions =
    useBeneficiaryDesignations || dispositionRowsByDeath !== null;

  return (
    <PresentationDataTableSlide
      id={slideId}
      slideTitle={entity.subtype.displayName}
      leftHeaderContent={
        <PresentationSlide.MainHeading
          heading={entity.subtype.displayName}
          subheading={entity.extendedDisplayKind}
        />
      }
      footer={footer ?? <PresentationSlide.Footer />}
    >
      <Stack px={PRESENTATION_SPACING_UNITS} py={1} spacing={2} height="100%">
        {showCurrentAccess && (
          <PresentationHoverRow
            id={`${slideId}-current-access`}
            toolbar={currentAccessToolbar}
            disabled={!currentAccessToolbar}
          >
            <PersonalAccountBeneficiariesV2
              key="personal-account-beneficiaries-v2"
              rows={beneficiariesRows}
              groupHeader={groupHeader}
              heading="Current beneficiaries"
            />
          </PresentationHoverRow>
        )}
        {showDispositions && (
          <PresentationHoverRow
            id={`${slideId}-dispositions`}
            toolbar={dispositionsToolbar}
            disabled={!dispositionsToolbar}
          >
            <DispositionScenarioTable
              key="personal-account-disposition-scenario-table"
              rowDataByDeath={dispositionRowsByDeath}
              primaryClients={grantors}
              dispositionScenario={dispositionScenario?.dispositionScenario}
              {...(useBeneficiaryDesignations
                ? { beneficiaryDesignations }
                : {})}
            />
          </PresentationHoverRow>
        )}
      </Stack>
    </PresentationDataTableSlide>
  );
}
